import React, { useEffect, useRef, useState } from "react";
import { UserAvatar, avatarUrl, roleLabel } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Camera, Trash2, ImagePlus, Save } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
export const fmtDateTime = (iso) => {
  const d = new Date(iso);
  return !iso || isNaN(d) ? "—" : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
};
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const PERSON_KEYS = ["document", "birth_date", "cep", "street", "number", "complement", "district", "city", "state"];

export const Card = ({ title, children, className = "" }) => (
  <section className={`bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 ${className}`}>
    {title && <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">{title}</h2>}
    {children}
  </section>
);

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-slate-700">{label}</Label>
    {children}
  </div>
);

/**
 * Capa + foto + detalhes + formulário de dados pessoais.
 * Usado em "Minha conta" (o próprio usuário) e na página do admin (aluno/professor).
 *  subject  : { name, email, avatar, cover, profession, role, profile, phone, bio }
 *  details  : { account_id, created_at, person: {document, birth_date, ...} }
 *  onSave(body), onUpload(kind, file), onRemove(kind): funções assíncronas
 *  uploadsDisabled: texto explicando por que foto/capa estão indisponíveis (ou vazio)
 */
const ROLE_OPTS = [["aluno", "Aluno(a)"], ["professor", "Professor(a)"], ["instrutor", "Instrutor(a)"], ["mentor", "Mentor(a)"], ["representante", "Representante Credenciado"]];
const STATUS_LABEL = { ativo: "Ativo", desativado: "Desativado", suspenso: "Suspenso", bloqueado: "Bloqueado" };

/** admin (opcional): { roleValue, onRoleChange(role), status, onStatusChange(status) } — só na tela do administrador */
export function PersonalProfile({ subject, details, isStudent, isInstructor, onSave, onUpload, onRemove, uploadsDisabled = "", admin = null }) {
  const avatarRef = useRef(null);
  const coverRef = useRef(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [emailLiberado, setEmailLiberado] = useState(false);
  const [emailNovo, setEmailNovo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [nomeLiberado, setNomeLiberado] = useState(false);   // só a escola (admin) pode liberar a edição do nome

  useEffect(() => {
    if (!subject || !details) return;
    setForm({
      name: subject.name || "", profession: subject.profession || "", phone: subject.phone || "", bio: subject.bio || "",
      profile: subject.profile || "professor", ...(details.person || {}),
      nationality: details.person?.nationality || "brasileiro", country: details.person?.country || "",
    });
  }, [subject?.name, details]); // eslint-disable-line react-hooks/exhaustive-deps

  const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const upload = async (kind, files) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(kind);
    try { await onUpload(kind, file); toast.success(kind === "avatar" ? "Foto atualizada!" : "Capa atualizada!"); }
    catch (e) { toast.error(errMsg(e)); }
    finally {
      setUploading("");
      if (avatarRef.current) avatarRef.current.value = "";
      if (coverRef.current) coverRef.current.value = "";
    }
  };
  const remove = async (kind) => { try { await onRemove(kind); } catch (e) { toast.error(errMsg(e)); } };

  // CEP: completa o endereço automaticamente (ViaCEP)
  const lookupCep = async () => {
    if (form.nationality === "estrangeiro") return;
    const d = (form.cep || "").replace(/\D/g, "");
    if (d.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${d}/json/`).then((x) => x.json());
      if (r.erro) return toast.error("CEP não encontrado.");
      setForm((p) => ({ ...p, street: r.logradouro || p.street, district: r.bairro || p.district, city: r.localidade || p.city, state: r.uf || p.state }));
    } catch (e) { /* sem acesso ao ViaCEP: segue manual */ }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { name: form.name, profession: form.profession, phone: form.phone, bio: form.bio };
      if (isStudent || isInstructor) PERSON_KEYS.forEach((k) => { if (k !== "birth_date" || isStudent) body[k] = form[k] || ""; });
      body.nationality = form.nationality;
      body.country = form.nationality === "estrangeiro" ? form.country : "Brasil";
      await onSave(body);
      toast.success("Dados atualizados!");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const cover = avatarUrl(subject?.cover);

  return (
    <>
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 min-h-[260px] sm:min-h-[300px]" data-testid="profile-cover">
        {cover ? <>
          <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
        </> : <>
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 25% 20%, #fff 0%, #F3F6FB 50%, #E6EDF6 100%)" }} />
          <img src="/brand/login-arte-frascos.webp" alt="" className="perfil-capa-frascos" />
          <img src="/brand/aluna-hero-arte.webp" alt="" className="perfil-capa-arte" />
        </>}
        {!uploadsDisabled && (
          <div className="absolute top-4 right-4 flex gap-2">
            <input ref={coverRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload("cover", e.target.files)} />
            {subject?.cover && (
              <Button size="sm" variant="secondary" onClick={() => remove("cover")} className="bg-white/90 text-slate-700" title="Remover capa"><Trash2 className="h-4 w-4" /></Button>
            )}
            <Button size="sm" onClick={() => coverRef.current?.click()} disabled={uploading === "cover"} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid="profile-cover-button">
              {uploading === "cover" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-1.5" /> Atualizar capa</>}
            </Button>
          </div>
        )}
        <div className="relative flex flex-col sm:flex-row sm:items-end gap-5 p-6 pt-20 sm:pt-24">
          <div className="relative w-fit">
            <UserAvatar user={subject} size={132} className="ring-4 ring-white shadow-xl" />
            {!uploadsDisabled && (
              <>
                <input ref={avatarRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload("avatar", e.target.files)} data-testid="profile-avatar-input" />
                <button onClick={() => avatarRef.current?.click()} disabled={uploading === "avatar"}
                  className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3 py-1.5 rounded-lg shadow" style={{ background: "#1E9E2A" }}>
                  {uploading === "avatar" ? <Loader2 className="h-4 w-4 animate-spin" /> : <>alterar foto <Camera className="h-4 w-4" /></>}
                </button>
              </>
            )}
          </div>
          <div className={`rounded-xl px-4 py-3 w-fit max-w-full ${cover ? "bg-black/45 backdrop-blur-sm text-white" : "bg-white/90 backdrop-blur-sm text-[#1A2340] shadow-lg ring-1 ring-slate-200/70"}`}>
            <div className="font-display text-2xl font-bold leading-tight">{subject?.name}</div>
            <div className={`text-sm break-all ${cover ? "text-white/90" : "text-slate-600"}`}>{subject?.email}</div>
            <div className={`text-sm ${cover ? "text-white/80" : "text-[#1E9E2A] font-semibold"}`}>{subject?.profession || roleLabel(subject)}</div>
          </div>
          {!uploadsDisabled && subject?.avatar && (
            <button onClick={() => remove("avatar")} className={`sm:ml-auto text-xs underline w-fit ${cover ? "text-white/80 hover:text-white" : "text-slate-500 hover:text-slate-800"}`}>remover foto</button>
          )}
        </div>
        {uploadsDisabled && <div className={`relative px-6 pb-4 text-xs ${cover ? "text-white/85" : "text-slate-500"}`}>{uploadsDisabled}</div>}
      </div>

      <Card title="Detalhes da conta">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="ID (data do cadastro)">
            <Input value={details?.account_id || "—"} readOnly className="font-mono campo-leitura" data-testid="profile-reg-code" />
            {details?.legacy_id && <p className="text-[11px] text-slate-400">ID na plataforma antiga: {details.legacy_id}</p>}
          </Field>
          <Field label="Data de cadastro"><Input value={fmtDateTime(details?.created_at)} readOnly className="campo-leitura" /></Field>
          <Field label="Perfil">
            {admin?.dual ? (
              // já tem as duas áreas (aluna e professora): não há o que converter, só abrir o outro cadastro
              <div className="flex items-center gap-2 h-10" data-testid="profile-role-dual">
                <span className="inline-flex items-center rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-2.5 py-1">Aluno(a) + {admin.dual.label}</span>
                <a href={admin.dual.href} className="text-xs font-semibold text-[#1E9E2A] hover:underline whitespace-nowrap">Abrir cadastro de {admin.dual.label.toLowerCase()} →</a>
              </div>
            ) : admin ? (
              <select value={admin.roleValue} onChange={(e) => admin.onRoleChange(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="profile-role-select">
                {ROLE_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : <Input value={roleLabel(subject)} readOnly className="campo-leitura" />}
          </Field>
          <Field label="Situação">
            {admin ? (
              <select value={["ativo", "desativado"].includes(admin.status) ? admin.status : admin.status} onChange={(e) => admin.onStatusChange(e.target.value)}
                className={`h-10 w-full rounded-md border px-3 text-sm font-medium ${admin.status === "ativo" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}`} data-testid="profile-status-select">
                <option value="ativo">Ativo</option>
                <option value="desativado">Desativado</option>
                {!["ativo", "desativado"].includes(admin.status) && <option value={admin.status}>{STATUS_LABEL[admin.status] || admin.status}</option>}
              </select>
            ) : <Input value={STATUS_LABEL[details?.access_status || "ativo"]} readOnly className="campo-leitura" />}
          </Field>
        </div>
      </Card>

      {!form ? (
        <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <form onSubmit={save} className="space-y-6">
          <Card title="Dados pessoais">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome completo *">
                {!(isStudent || isInstructor) ? <Input value={form.name} onChange={up("name")} required data-testid="profile-name-input" /> : (
                  <>
                    <div className="flex gap-2">
                      <Input value={form.name} onChange={up("name")} readOnly={!nomeLiberado} required className={nomeLiberado ? "" : "bg-slate-50"} data-testid="profile-name-input" />
                      {admin && !nomeLiberado && (
                        <button type="button" onClick={async () => (await confirmar("O nome consta no contrato, nos certificados e nas notas fiscais. Alterar mesmo assim?")) && setNomeLiberado(true)}
                          className="px-3 h-10 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 whitespace-nowrap" data-testid="profile-name-unlock">Alterar nome</button>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">O nome consta no contrato, nos certificados e nas notas fiscais. {admin ? "Bloqueado para evitar alterações acidentais: só a escola altera, pelo botão ao lado." : "Para corrigir, fale com a escola."}</p>
                  </>
                )}
              </Field>
              <Field label="E-mail">
                <div className="flex gap-2">
                  <Input value={emailLiberado ? emailNovo : (subject?.email || "")} onChange={(e) => setEmailNovo(e.target.value)} readOnly={!emailLiberado} type="email" className={emailLiberado ? "" : "bg-slate-50"} data-testid="profile-email" />
                  {admin?.onEmailChange && !emailLiberado && (
                    <button type="button" onClick={async () => (await confirmar("O e-mail é o login da pessoa e recebe o contrato e a nota fiscal. Alterar mesmo assim?")) && (setEmailNovo(subject?.email || ""), setEmailLiberado(true))}
                      className="px-3 h-10 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 whitespace-nowrap" data-testid="profile-email-unlock">Alterar e-mail</button>
                  )}
                  {emailLiberado && (
                    <>
                      <button type="button" disabled={emailBusy} onClick={async () => { setEmailBusy(true); try { await admin.onEmailChange(emailNovo); toast.success("E-mail alterado."); setEmailLiberado(false); } catch (e) { toast.error(errMsg(e)); } finally { setEmailBusy(false); } }}
                        className="px-3 h-10 rounded-md text-white text-sm font-semibold whitespace-nowrap" style={{ background: "#1E9E2A" }} data-testid="profile-email-save">{emailBusy ? "Salvando…" : "Salvar e-mail"}</button>
                      <button type="button" onClick={() => setEmailLiberado(false)} className="px-3 h-10 rounded-md border border-slate-200 text-sm">Cancelar</button>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">É o e-mail de acesso e também recebe o contrato e a nota fiscal. {admin?.onEmailChange ? "Bloqueado para evitar alterações acidentais: só a escola altera, pelo botão ao lado." : "Para trocar, fale com a escola."}</p></Field>
              <Field label="Profissão"><Input value={form.profession} onChange={up("profession")} placeholder="Ex.: Cabeleireira" /></Field>
              <Field label="Telefone / WhatsApp"><Input value={form.phone} onChange={up("phone")} placeholder="(00) 00000-0000" /></Field>
              <Field label="Nacionalidade">
                <select value={form.nationality} onChange={up("nationality")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="profile-nationality">
                  <option value="brasileiro">Brasileiro(a)</option>
                  <option value="estrangeiro">Estrangeiro(a)</option>
                </select>
              </Field>
              {form.nationality === "estrangeiro" && (
                <Field label="País"><Input value={form.country || ""} onChange={up("country")} placeholder="Ex.: Portugal" data-testid="profile-country" /></Field>
              )}
              {(isStudent || isInstructor) && (
                <Field label={form.nationality === "estrangeiro" ? "Passaporte / documento" : (isInstructor ? "CPF ou CNPJ" : "CPF / Documento")}>
                  <Input value={form.document || ""} onChange={up("document")} placeholder={form.nationality === "estrangeiro" ? "Nº do passaporte ou documento" : "000.000.000-00"} />
                </Field>
              )}
              {isStudent && <Field label="Data de nascimento"><Input type="date" value={form.birth_date || ""} onChange={up("birth_date")} /></Field>}
              <Field label="Sobre" className="sm:col-span-2">
                <Textarea rows={3} maxLength={600} value={form.bio} onChange={up("bio")} placeholder="Uma breve apresentação (opcional)" />
              </Field>
            </div>
          </Card>

          {(isStudent || isInstructor) && (
            <Card title="Endereço">
              <div className="grid sm:grid-cols-6 gap-4">
                <Field label={form.nationality === "estrangeiro" ? "Código postal" : "CEP"} className="sm:col-span-2">
                  <Input value={form.cep || ""} onChange={up("cep")} onBlur={lookupCep} placeholder={form.nationality === "estrangeiro" ? "" : "00000-000"} />
                </Field>
                <Field label="Rua / Logradouro" className="sm:col-span-4"><Input value={form.street || ""} onChange={up("street")} /></Field>
                <Field label="Número" className="sm:col-span-1"><Input value={form.number || ""} onChange={up("number")} /></Field>
                <Field label="Complemento" className="sm:col-span-2"><Input value={form.complement || ""} onChange={up("complement")} /></Field>
                <Field label="Bairro" className="sm:col-span-3"><Input value={form.district || ""} onChange={up("district")} /></Field>
                <Field label="Cidade" className="sm:col-span-4"><Input value={form.city || ""} onChange={up("city")} /></Field>
                <Field label={form.nationality === "estrangeiro" ? "Estado / Província" : "Estado (UF)"} className="sm:col-span-2">
                  {form.nationality === "estrangeiro" ? (
                    <Input value={form.state || ""} onChange={up("state")} />
                  ) : (
                    <select value={form.state || ""} onChange={up("state")} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                      <option value="">—</option>
                      {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  )}
                </Field>
              </div>
            </Card>
          )}

          <div className="sticky bottom-20 md:bottom-4 z-10 flex justify-end">
            <Button type="submit" disabled={saving} className="h-11 text-white px-8 w-full sm:w-auto shadow-[0_12px_28px_-10px_rgba(30,158,42,.7)]" style={{ background: "#1E9E2A" }} data-testid="profile-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Salvar dados</>}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
